import React, { useEffect, useState } from "react";
import { Button, Form, notification, Popconfirm, Rate, Typography } from "antd";
import { useParams } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import {
  IBettingWebsite,
  IBettingWebsiteRatingSubRates,
} from "../../../types/BettingWebsite";
import { editBettingWebsiteRating, getBettingWebsiteByID } from "../action";

const { Text } = Typography;

type Props = {
  fetchData: () => void;
};

const labelMap: Record<keyof IBettingWebsiteRatingSubRates, string> = {
  Secuirity: "Security",
  Reputation: "Reputation",
  Transparency: "Transparency",
  Popularity: "Popularity",
  UserExperience: "User Experience",
  UserInterface: "User Interface",
  BonusAmount: "Bonus Amount",
  VarietyOfOptions: "Variety of Options",
  MarketDiversity: "Market Diversity",
  TransactionSpeed: "Transaction Speed",
  BettingOdds: "Betting Odds",
  SupportQuality: "Support Quality",
  BonusType: "Bonus Type",
  VarietyOfTransactionMethods: "Transaction Methods",
};

const EditRatings: React.FC<Props> = ({ fetchData }) => {
  const { id } = useParams();
  const [form] = Form.useForm<IBettingWebsiteRatingSubRates>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const fetchRating = async () => {
    setIsLoading(true);
    try {
      const rating: IBettingWebsite | null = await getBettingWebsiteByID(id!);
      if (rating) form.setFieldsValue(rating.Ratings.SubRates);
    } catch (err: any) {
      api.error({
        message: "Failed to fetch rating",
        description: err?.message || "Unexpected error occurred",
      });
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const success = await editBettingWebsiteRating(id!, values);

      if (success) {
        fetchData();
        api.success({ message: "Ratings updated successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update ratings",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchRating();
  }, [id]);

  return (
    <Form form={form} layout="vertical" className="space-y-4">
      {contextHolder}
      <div className=" grid grid-cols-4 gap-4">
        {(Object.keys(labelMap) as (keyof IBettingWebsiteRatingSubRates)[]).map(
          (key) => (
            <Form.Item
              key={key}
              label={labelMap[key]}
              name={key}
              rules={[
                { required: true, message: `Please rate ${labelMap[key]}` },
              ]}
            >
              <Rate allowHalf count={5} />
            </Form.Item>
          )
        )}
      </div>
      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to update ratings?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<FaEdit />}
              loading={isLoading}
              type="primary"
              className="w-full"
              disabled={
                !form.isFieldsTouched(true) ||
                form.getFieldsError().some((f) => f.errors.length > 0)
              }
            >
              {isLoading ? "Updating Ratings..." : "Edit Ratings"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditRatings;
